import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Sparkles, Dices } from 'lucide-react';
import { IngredientInput } from './IngredientInput';
import type { GenerateRequest, MixType, Profile } from '@/shared/types';
import { useModelStore } from '@/shared/stores/model';

const profiles: { value: Profile; label: string }[] = [
  { value: 'balanced', label: 'Сбалансированный' },
  { value: 'sweet', label: 'Сладкий' },
  { value: 'sour', label: 'Кислый' },
  { value: 'strong', label: 'Крепкий' },
  { value: 'refreshing', label: 'Освежающий' },
  { value: 'fruity', label: 'Фруктовый' },
  { value: 'creamy', label: 'Сливочный' },
  { value: 'smoky', label: 'Дымный' },
  { value: 'spicy', label: 'Пряный' },
];

const generateSchema = z.object({
  type: z.enum(['cocktail', 'hookah']),
  profile: z.enum([
    'balanced',
    'sweet',
    'sour',
    'strong',
    'refreshing',
    'fruity',
    'creamy',
    'smoky',
    'spicy',
  ]),
  ingredients: z.array(z.object({ name: z.string(), amount: z.string().optional(), unit: z.string().optional() })),
  notes: z.string().max(1000),
  alcoholic: z.boolean().optional(),
  onlyAvailable: z.boolean().optional(),
});

type FormData = z.infer<typeof generateSchema>;

interface GenerateFormProps {
  onGenerate: (request: GenerateRequest) => void;
  onRandom: (request: Omit<GenerateRequest, 'ingredients' | 'constraints'>) => void;
  isLoading: boolean;
}

export function GenerateForm({ onGenerate, onRandom, isLoading }: GenerateFormProps): JSX.Element {
  const { model } = useModelStore();
  const { register, handleSubmit, control, watch } = useForm<FormData>({
    resolver: zodResolver(generateSchema),
    defaultValues: {
      type: 'cocktail',
      profile: 'balanced',
      ingredients: [],
      notes: '',
      alcoholic: undefined,
      onlyAvailable: false,
    },
  });

  const type = watch('type');

  const onSubmit = (data: FormData): void => {
    const request: GenerateRequest = {
      type: data.type,
      profile: data.profile,
      ingredients: data.ingredients.length > 0 ? data.ingredients : undefined,
      notes: data.notes.length > 0 ? data.notes : undefined,
      model,
      constraints: {
        alcoholic: data.type === 'cocktail' ? data.alcoholic : undefined,
        onlyAvailable: data.onlyAvailable,
      },
    };

    onGenerate(request);
  };

  const handleRandom = (): void => {
    onRandom({ type: type as MixType, profile: watch('profile'), notes: watch('notes') || undefined, model });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Что будем миксовать?</label>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => field.onChange('cocktail')}
                className={`rounded-xl border px-4 py-3 text-sm font-medium transition-all ${
                  field.value === 'cocktail'
                    ? 'border-pink-500/50 bg-pink-500/10 text-pink-700 dark:text-pink-300'
                    : 'border-black/10 bg-white/50 text-slate-600 hover:border-black/20 dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-400 dark:hover:border-white/20'
                }`}
              >
                🍸 Коктейль
              </button>
              <button
                type="button"
                onClick={() => field.onChange('hookah')}
                className={`rounded-xl border px-4 py-3 text-sm font-medium transition-all ${
                  field.value === 'hookah'
                    ? 'border-purple-500/50 bg-purple-500/10 text-purple-700 dark:text-purple-300'
                    : 'border-black/10 bg-white/50 text-slate-600 hover:border-black/20 dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-400 dark:hover:border-white/20'
                }`}
              >
                🌬 Кальян
              </button>
            </div>
          )}
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Профиль вкуса</label>
        <Controller
          name="profile"
          control={control}
          render={({ field }) => (
            <div className="flex flex-wrap gap-2">
              {profiles.map((profile) => (
                <button
                  key={profile.value}
                  type="button"
                  onClick={() => field.onChange(profile.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                    field.value === profile.value
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'bg-black/5 text-slate-600 hover:bg-black/10 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-white/10'
                  }`}
                >
                  {profile.label}
                </button>
              ))}
            </div>
          )}
        />
      </div>

      <Controller
        name="ingredients"
        control={control}
        render={({ field }) => <IngredientInput ingredients={field.value} onChange={field.onChange} />}
      />

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Дополнительно</label>
        {type === 'cocktail' && (
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              {...register('alcoholic')}
              className="h-4 w-4 rounded border-black/10 bg-white/50 text-pink-500 focus:ring-pink-500/20 dark:border-white/10 dark:bg-slate-900/50"
            />
            Алкогольный
          </label>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            {...register('onlyAvailable')}
            className="h-4 w-4 rounded border-black/10 bg-white/50 text-pink-500 focus:ring-pink-500/20 dark:border-white/10 dark:bg-slate-900/50"
          />
          Только указанные ингредиенты
        </label>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Пожелания</label>
        <textarea
          {...register('notes')}
          rows={3}
          placeholder="Например: лёгкий летний коктейль для вечеринки"
          className="w-full rounded-xl border border-black/10 bg-white/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-pink-500/50 focus:outline-none focus:ring-2 focus:ring-pink-500/20 dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>

      <div className="space-y-2 pt-2">
        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-pink-500/20 transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Придумываем миксы...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Сгенерировать
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleRandom}
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-black/10 bg-white/50 py-3 text-sm font-medium text-slate-700 transition-all hover:bg-black/5 active:scale-[0.98] disabled:opacity-60 dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-300 dark:hover:bg-white/5"
        >
          <Dices className="h-4 w-4" />
          Случайный микс
        </button>
      </div>
    </form>
  );
}
